import {
  useQuery,
  QueryObserverResult,
  UseQueryOptions
} from '@tanstack/react-query'
import { secondsToMilliseconds } from 'date-fns'

import { fetchUserDetails } from '@pepper/utils/src/api/users/users'
import handleException from '@pepper/utils/src/function/handleException'

type ReturnType = {
  userDetails?: UserDetails
  isUserDetailsLoading: boolean
  userDetailsError: Error | null
  refetchUserDetails: QueryObserverResult<UserDetails, Error>['refetch']
}

const DEFAULT_STALE_TIME = 30

export const useUserDetails = (
  userId?: string | null,
  config?: UseQueryOptions<UserDetails, Error>
): ReturnType => {
  const {
    data: userDetails,
    isLoading: isUserDetailsLoading,
    error: userDetailsError,
    refetch: refetchUserDetails
  } = useQuery<UserDetails, Error>({
    queryKey: [`/users/${userId}/details`],
    queryFn: async () => {
      if (typeof userId !== 'string') throw new Error('Invalid user id.')

      const {
        status,
        data: _userDetails,
        error
      } = await fetchUserDetails({ id: userId })

      if (status === 'error') throw new Error(error)
      if (!_userDetails) throw new Error('User details not found.')

      return _userDetails
    },
    enabled: Boolean(userId),
    staleTime: secondsToMilliseconds(DEFAULT_STALE_TIME),
    onError: _error => handleException(_error),
    ...config
  })

  return {
    userDetails,
    isUserDetailsLoading,
    userDetailsError,
    refetchUserDetails
  }
}
